<template>
  <div class="config-page">
    <van-nav-bar left-arrow class="crystal-nav" @click-left="gotohome">
      <template #left>
        <van-icon name="arrow-left" size="20px" color="#475569" />
      </template>
      <template #title>
        <span class="nav-title">参数设置</span>
      </template>
    </van-nav-bar>

    <div class="content-area">
      <!-- 设备告警锁定提示: 按协议(文档5.3.1)告警状态下设备拒绝参数写入,
           这里显式说明原因并提供一键清除, 避免"点了没反应" -->
      <div v-if="hasDeviceError" class="alarm-banner">
        <div class="alarm-banner-text">
          <div class="alarm-banner-title">设备告警锁定，暂不能修改参数</div>
          <div class="alarm-banner-sub">请先清除告警；若为污水桶满，需先清空污水桶</div>
        </div>
        <button class="alarm-banner-btn" :disabled="loading" @click="clearDeviceAlarm">清除告警</button>
      </div>
      <van-tabs v-model="activeTab" color="#2dd4bf" title-active-color="#0d9488" class="crystal-tabs">
        <van-tab title="补水设置">
          <div class="tab-content">
            <div class="config-block">
              <div class="config-item">
                <span class="config-label">补水开关</span>
                <van-switch v-model="config_re[0]" size="22" />
              </div>
              <div class="config-divider"></div>
              <div class="config-item">
                <span class="config-label">补水间隔（小时）</span>
                <div class="config-stepper">
                  <van-stepper v-model="config_re[1]" min="1" max="24" step="1" />
                </div>
              </div>
              <div class="config-divider"></div>
              <div class="config-item">
                <span class="config-label">独立补水超时（分钟）</span>
                <div class="config-stepper">
                  <van-stepper v-model="config_re[2]" min="0" max="99" step="1" />
                </div>
              </div>
              <div class="config-hint">仅用于自动独立补水；单独补水和换水补水用「安全时间」里的换水补水超时。</div>
            </div>
            <div class="config-save-wrap">
              <button class="save-btn" :disabled="loading || hasDeviceError" @click="saveConfig">
                <span v-if="loading" class="save-btn-loading"></span>
                <span>{{ loading ? '保存中...' : '保存补水设置' }}</span>
              </button>
            </div>
          </div>
        </van-tab>

        <van-tab title="换水设置">
          <div class="tab-content">
            <div class="config-block">
              <!-- 换水计划: 决定什么时候换水; 换水方式: 决定换多少水 (文档修改项30/31/39) -->
              <!-- 换水计划: 3 选项单选行放不下, 改点击弹出选择器 -->
              <div class="config-item" @click="openPlanPicker">
                <span class="config-label">换水计划</span>
                <div class="time-picker-trigger">
                  <span class="time-picker-text">{{ planText }}</span>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M11 5L7 9L3 5" stroke="#94a3b8" stroke-width="1.5" stroke-linecap="round"
                      stroke-linejoin="round" />
                  </svg>
                </div>
              </div>

              <div class="config-divider"></div>
              <div class="config-item">
                <span class="config-label">换水方式</span>
                <div class="config-select">
                  <!-- radio 的 name 必须是数字, 与设备回读的 0/1 严格相等才显示选中;
                       数组下标直接 v-model 在 Vue2 不触发重渲染, 故走 computed 的 get/set -->
                  <van-radio-group v-model="amountMode" direction="horizontal">
                    <van-radio :name="0" class="crystal-radio">区间换水</van-radio>
                    <van-radio :name="1" class="crystal-radio">定量换水</van-radio>
                  </van-radio-group>
                </div>
              </div>
              <!-- 定量换水时才显示每次换水量, 区间时隐藏 (文档修改项32) -->
              <template v-if="amountMode === 1">
                <div class="config-divider"></div>
                <div class="config-item">
                  <span class="config-label">每次换水量（L）</span>
                  <div class="config-stepper">
                    <van-stepper v-model="fixedLiter" min="1" max="999" step="1" />
                  </div>
                </div>
              </template>
            </div>
            <div class="config-hint">
              换水计划决定什么时候启动换水；换水方式决定换多少水，两者需分别设置。
            </div>

            <!-- 间隔计划: 仅"间隔换水"时显示 (文档修改项33) -->
            <div class="config-block" v-if="isWcInterval">
              <div class="config-item">
                <span class="config-label">换水间隔（小时）</span>
                <div class="config-stepper">
                  <van-stepper v-model="config_wc[1]" min="1" max="168" step="1" />
                </div>
              </div>
            </div>

            <!-- 预约计划: 默认折叠, 只显示星期/时间摘要+开关, 点击进详细设置 (文档修改项34) -->
            <div class="config-block" v-if="isWcScheduled">
              <div class="block-title">预约设置</div>
              <template v-for="g in [0, 1, 2]">
                <div :key="'rd' + g" v-if="g > 0" class="config-divider"></div>
                <div :key="'rg' + g" class="rtc-row">
                  <div class="rtc-row-main" @click="toggleRtcExpand(g)">
                    <span class="rtc-row-name">预约{{ g + 1 }}</span>
                    <span class="rtc-row-summary" :class="{ 'rtc-row-summary--unset': rtcSummary(g) === '未设置' }">
                      {{ rtcSummary(g) }}
                    </span>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none"
                      :style="{ transform: expandedRtc === g ? 'rotate(90deg)' : 'none', transition: 'transform .2s' }">
                      <path d="M5 3L9 7L5 11" stroke="#94a3b8" stroke-width="1.5" stroke-linecap="round"
                        stroke-linejoin="round" />
                    </svg>
                  </div>
                  <van-switch :value="config_wc[4 + g * 4] === 1" size="20"
                    @change="setRtcEnable(g, $event)" class="rtc-row-switch" />
                </div>
                <!-- 展开的详细设置: 星期多选 + 时间 -->
                <div v-if="expandedRtc === g" :key="'re' + g" class="rtc-detail">
                  <div class="config-divider"></div>
                  <div class="config-item config-item--full">
                    <div class="config-weekdays">
                      <span v-for="(day, idx) in weekdays" :key="'wd' + g + idx" class="weekday-tag"
                        :class="{ 'weekday-active': (config_wc[5 + g * 4] >> idx) & 1 }"
                        @click="toggleWeekday(g, idx)">
                        {{ day }}
                      </span>
                    </div>
                  </div>
                  <div class="config-divider"></div>
                  <div class="config-item">
                    <span class="config-label">时间</span>
                    <div class="time-picker-trigger" @click="openTimePicker(g)">
                      <span class="time-picker-text">{{ rtcTimeDisplay(g) }}</span>
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path d="M11 5L7 9L3 5" stroke="#94a3b8" stroke-width="1.5" stroke-linecap="round"
                          stroke-linejoin="round" />
                      </svg>
                    </div>
                  </div>
                </div>
              </template>
              <div class="config-hint">每组需打开右侧开关、并至少选中一天，才会按时执行（换水计划需为「预约换水」）；关闭的组只保留星期和时间，标注「未启用」。</div>
            </div>

            <!-- 安全时间: 排水/换水补水超时统一放这里, 0=关闭 (文档修改项35/37) -->
            <div class="config-block">
              <div class="block-title">安全时间</div>
              <div class="config-item">
                <span class="config-label">排水超时（分钟）</span>
                <div class="config-stepper">
                  <van-stepper v-model="config_wc[2]" min="0" max="99" step="1" />
                </div>
              </div>
              <div class="config-divider"></div>
              <div class="config-item">
                <span class="config-label">换水补水超时（分钟）</span>
                <div class="config-stepper">
                  <van-stepper v-model="config_wc[3]" min="0" max="99" step="1" />
                </div>
              </div>
              <div class="config-hint">设置为 0 表示关闭超时保护</div>
            </div>

            <div class="config-save-wrap">
              <button class="save-btn" :disabled="loading || hasDeviceError" @click="saveConfig">
                <span v-if="loading" class="save-btn-loading"></span>
                <span>{{ loading ? '保存中...' : '保存换水设置' }}</span>
              </button>
            </div>
          </div>
        </van-tab>

        <van-tab title="系统设置">
          <div class="tab-content">
            <div class="config-block">
              <div class="config-item">
                <span class="config-label">维护模式时长（分钟）</span>
                <div class="config-stepper">
                  <van-stepper v-model="config_sys[0]" min="1" max="99" step="1" />
                </div>
              </div>
              <div class="config-divider"></div>
              <div class="config-item">
                <span class="config-label">困水桶缺水检测</span>
                <van-switch v-model="config_sys[1]" size="22" />
              </div>
              <div class="config-divider"></div>
              <div class="config-item">
                <span class="config-label">蜂鸣器报警</span>
                <van-switch v-model="config_sys[2]" size="22" />
              </div>
              <div class="config-divider"></div>
              <!-- 每升脉冲数自换水设置移入系统设置 (文档修改项36/43) -->
              <div class="config-item">
                <span class="config-label">每升脉冲数（脉冲/L）</span>
                <div class="config-stepper">
                  <van-stepper v-model="config_wc[18]" min="1" max="9999" step="1" />
                </div>
              </div>
              <div class="config-hint">出厂已校准，非必要请勿修改</div>
            </div>
            <div class="config-save-wrap">
              <button class="save-btn" :disabled="loading || hasDeviceError" @click="saveConfig">
                <span v-if="loading" class="save-btn-loading"></span>
                <span>{{ loading ? '保存中...' : '保存系统设置' }}</span>
              </button>
            </div>
          </div>
        </van-tab>
      </van-tabs>
    </div>

    <van-popup v-model="showTimePicker" position="bottom" round>
      <van-datetime-picker v-model="timePickerCurrent" type="time" :min-hour="0" :max-hour="23" :min-minute="0"
        :max-minute="59" @confirm="onTimeConfirm" @cancel="showTimePicker = false" />
    </van-popup>

    <van-popup v-model="showPlanPicker" position="bottom" round>
      <van-picker ref="planPicker" show-toolbar title="换水计划" :columns="planColumns" @confirm="onPlanConfirm"
        @cancel="showPlanPicker = false" />
    </van-popup>
  </div>
</template>

<script>
export default {
  name: "KD6Config",
  data() {
    return {
      activeTab: 0,
      did: this.$route.query.did,
      weekdays: ["一", "二", "三", "四", "五", "六", "日"],
      config_re: [false, 1, 0],
      /* wc[19] = [运行模式P01, 间隔P02, 排水超时P08, 补水超时P09, 预约组1~3(开关/星期/时/分),
                   换水量模式P04, 定量升数P05, 每升脉冲数P12] */
      config_wc: [0, 24, 0, 0, 0, 0, 8, 0, 0, 0, 20, 0, 0, 0, 0, 0, 0, 10, 1920],
      config_sys: [30, false, true],
      loading: false,
      devErr: [],                /* 设备 err 数组 (非0=有告警锁定, 此时设备拒收参数) */
      showTimePicker: false,
      showPlanPicker: false,     /* 换水计划选择弹窗 */
      planColumns: [{ values: ["间隔换水", "预约换水", "仅手动"] }],
      currentRtcGroup: 0,        /* 时间选择器当前编辑的预约组 0~2 */
      timePickerCurrent: "08:00",
      expandedRtc: null,         /* 当前展开详细设置的预约组 0~2, null=全部折叠 */
    };
  },
  computed: {
    /* 换水方式 P04: computed get/set 保证 radio 回显(数字 0/1 严格相等) + 写入 $set(可触发重渲染) */
    amountMode: {
      get() {
        return Number(this.config_wc[16]) === 1 ? 1 : 0;
      },
      set(val) {
        this.$set(this.config_wc, 16, Number(val) === 1 ? 1 : 0);
      },
    },
    /* 定量换水量 P05 (升) */
    fixedLiter: {
      get() {
        var v = Number(this.config_wc[17]);
        return v >= 1 ? v : 1;
      },
      set(val) {
        var v = parseInt(val, 10);
        this.$set(this.config_wc, 17, v >= 1 ? v : 1);
      },
    },
    /* 设备是否处于告警锁定 (值为 err 数组, 无故障时设备上报 [0]) */
    hasDeviceError() {
      var e = this.devErr || [];
      for (var i = 0; i < e.length; i++) {
        if (Number(e[i]) !== 0) return true;
      }
      return false;
    },
    /* 换水计划当前值文案 (config_wc[0]: 0间隔 1预约 2仅手动) */
    planText() {
      var i = Number(this.config_wc[0]);
      return this.planColumns[0].values[i] || this.planColumns[0].values[0];
    },
    isWcInterval() {
      return String(this.config_wc[0]) === "0";
    },
    isWcScheduled() {
      return String(this.config_wc[0]) === "1";
    },
    isWcManual() {
      return String(this.config_wc[0]) === "2";
    },
  },
  watch: {
    config_wc: {
      handler(val) {
        if (val && val.length >= 13) {
          var h = String(Number(val[6 + this.currentRtcGroup * 4]) || 0).padStart(2, "0");
          var m = String(Number(val[7 + this.currentRtcGroup * 4]) || 0).padStart(2, "0");
          this.timePickerCurrent = h + ":" + m;
        }
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    /* 预约组折叠行摘要: 已启用显示"星期、时间", 否则"未设置" (文档修改项34) */
    /* 预约组摘要: 已设星期/时间的组即使开关未打开也照实显示, 只标注"未启用"——
       否则用户会误以为刚保存的预约没存上 (V2.0 修改项05) */
    rtcSummary(g) {
      var wk = Number(this.config_wc[5 + g * 4]) || 0;
      var on = Number(this.config_wc[4 + g * 4]) === 1;
      /* 设备侧要求: 开关打开 + 至少选中一天 才会执行; 只设时间不选星期 = 该组永不触发,
         必须显式提示, 否则看起来像"预约没保存" */
      if (!wk) return on ? "未选星期·不会执行" : "未设置";
      var dayNames = ["周一", "周二", "周三", "周四", "周五", "周六", "周日"];
      var names = [];
      for (var i = 0; i < 7; i++) {
        if ((wk >> i) & 1) names.push(dayNames[i]);
      }
      var h = String(Number(this.config_wc[6 + g * 4]) || 0).padStart(2, "0");
      var m = String(Number(this.config_wc[7 + g * 4]) || 0).padStart(2, "0");
      var text = names.join("、") + " " + h + ":" + m;
      if (!on) text += "（未启用）";
      return text;
    },
    /* 点击行展开/折叠详细设置 */
    toggleRtcExpand(g) {
      this.expandedRtc = this.expandedRtc === g ? null : g;
    },
    /* 指定预约组的时间显示 时:分 */
    rtcTimeDisplay(g) {
      var h = String(Number(this.config_wc[6 + g * 4]) || 0).padStart(2, "0");
      var m = String(Number(this.config_wc[7 + g * 4]) || 0).padStart(2, "0");
      return h + ":" + m;
    },
    /* 打开时间选择器并记录当前编辑的预约组 */
    openTimePicker(g) {
      this.currentRtcGroup = g;
      var h = String(Number(this.config_wc[6 + g * 4]) || 0).padStart(2, "0");
      var m = String(Number(this.config_wc[7 + g * 4]) || 0).padStart(2, "0");
      this.timePickerCurrent = h + ":" + m;
      this.showTimePicker = true;
    },
    /* 换水计划选择: 打开时定位到当前值 */
    openPlanPicker() {
      this.showPlanPicker = true;
      var idx = Number(this.config_wc[0]);
      if (!(idx >= 0 && idx < this.planColumns[0].values.length)) idx = 0;
      var self = this;
      this.$nextTick(function () {
        var picker = self.$refs.planPicker;
        if (picker) picker.setIndexes([idx]);
      });
    },
    onPlanConfirm(values) {
      var idx = this.planColumns[0].values.indexOf(values[0]);
      if (idx >= 0) this.$set(this.config_wc, 0, idx);
      this.showPlanPicker = false;
    },
    /* 把设备/后端返回的配置写入界面状态 (GetConfig 与保存回执共用) */
    applyConfig(cfg) {
      if (!cfg) return;
      if (cfg.re) {
        var re = cfg.re.slice();
        re[0] = Boolean(re[0]);
        this.config_re = re;
      }
      if (cfg.wc) {
        var wc = cfg.wc.slice();
        wc[0] = String(wc[0]);
        /* 补齐到 19 项（预约3组+换水量3项），防止数组不足导致 undefined */
        while (wc.length < 19) wc.push(wc.length === 18 ? 1920 : 0);
        this.config_wc = wc;
      }
      if (cfg.sys) {
        var arr = cfg.sys.slice();
        while (arr.length < 3) arr.push(0);
        arr[1] = Boolean(arr[1]);
        arr[2] = Boolean(arr[2]);
        this.config_sys = arr;
      }
    },
    GetConfig() {
      const qs = require("qs");
      this.$axios
        .post("/api/dev/one", qs.stringify({ did: this.did }))
        .then((response) => {
          var cfg = response.data.error_code === 0 ? response.data.config : null;
          if (response.data.err) this.devErr = response.data.err;
          if (cfg && cfg.wc) {
            this.applyConfig(cfg);
          } else {
            /* 后台缓存里还没有配置(设备刚上电/还没上报过 config)时,
               主动让设备回一次(ack 带完整 config, 后端随响应透传), 否则页面只能显示本地默认值 */
            this.pullConfigFromDevice();
          }
        })
        .catch(() => { this.pullConfigFromDevice(); });
    },
    /* 让设备立即上报一次当前配置: {a:2} 为设备协议的"刷新"请求, 应答 reconfig 里带 config */
    pullConfigFromDevice() {
      const qs = require("qs");
      this.$axios
        .post("/api/dev/easy_mqtt_config", qs.stringify({
          did: this.did,
          d_type: "KD-6",
          config: JSON.stringify({ a: 2 }),
        }))
        .then((response) => {
          if (response.data.error_code === 0 && response.data.config) {
            this.applyConfig(response.data.config);
          }
        })
        .catch(() => { });
    },
    /* 清除设备告警 (故障态下设备唯一接受的命令, 文档19.3.3), 之后刷新告警状态 */
    clearDeviceAlarm() {
      const qs = require("qs");
      this.$axios
        .post("/api/dev/easy_mqtt_config", qs.stringify({
          did: this.did,
          d_type: "KD-6",
          config: JSON.stringify({ act: { clr_err: 1 } }),
        }))
        .then((response) => {
          if (response.data.error_code === 0) {
            this.$toast.success("已发送清除告警，若仍显示请检查污水桶或浮球");
            this.GetConfig();
          } else {
            this.$toast.fail("清除告警失败，请到设备首页操作");
          }
        })
        .catch(() => { this.$toast.fail("清除告警失败"); });
    },
    saveConfig() {
      this.loading = true;
      const qs = require("qs");
      var configData = {};
      if (this.activeTab === 0) {
        configData = {
          replenish_enable: this.config_re[0] ? 1 : 0,
          replenish_Interval: this.config_re[1],
          replenish_timeout: this.config_re[2],
        };
      } else if (this.activeTab === 1) {
        configData = {
          wc_mode: Number(this.config_wc[0]),
          wc_interval: this.config_wc[1],
          drain_time: this.config_wc[2],
          wc_replenish_timeout: this.config_wc[3],
          /* 预约3组：开关/星期/时/分 */
          wc_rtc_enable1: this.config_wc[4],
          wc_cycle_week1: this.config_wc[5],
          wc_fixed_hour1: this.config_wc[6],
          wc_fixed_minute1: this.config_wc[7],
          wc_rtc_enable2: this.config_wc[8],
          wc_cycle_week2: this.config_wc[9],
          wc_fixed_hour2: this.config_wc[10],
          wc_fixed_minute2: this.config_wc[11],
          wc_rtc_enable3: this.config_wc[12],
          wc_cycle_week3: this.config_wc[13],
          wc_fixed_hour3: this.config_wc[14],
          wc_fixed_minute3: this.config_wc[15],
          /* KD-6 换水参数: P04 区间/定量, P05 定量升数
             (P12 每升脉冲数已移到系统设置页保存, 文档修改项36/43)
             用 computed 取值, 保证落在设备允许范围 (P04 0/1, P05 1~999) */
          wc_amount_mode: this.amountMode,
          wc_fixed_liter: this.fixedLiter,
        };
      } else if (this.activeTab === 2) {
        configData = {
          maintain_time: this.config_sys[0],
          no_water: this.config_sys[1] ? 1 : 0,
          alarm_beep: this.config_sys[2] ? 1 : 0,
          /* P12 每升脉冲数随系统设置一起保存 (文档修改项43) */
          flow_pulse_per_liter: Number(this.config_wc[18]),
        };
      }
      this.$axios
        .post("/api/dev/easy_mqtt_config", qs.stringify({
          did: this.did,
          d_type: "KD-6",
          config: JSON.stringify(configData),
        }))
        .then((response) => {
          this.loading = false;
          if (response.data.error_code === 0) {
            this.$toast.success("设置已保存");
            /* 直接用设备回执里的配置刷新界面(设备已确认保存的值), 不再依赖
               紧随其后的缓存回读 -- 避免与后台异步落缓存的竞态导致"看起来没保存" */
            if (response.data.config) {
              this.applyConfig(response.data.config);
            } else {
              this.GetConfig();
            }
          } else if (response.data.error_code === 407) {
            /* 设备已应答但未执行(忙/故障锁定/参数无效/状态不允许), 或 5 秒内未应答 */
            var ret = response.data.ret;
            var msg = "设备未保存：5 秒内无应答，请确认设备在线后重试";
            if (ret === 1) msg = "设备忙：请把设备面板退回主页，等当前任务结束后重试";
            else if (ret === 2) {
              msg = "设备告警锁定：参数已按协议拒绝，请先清除顶部告警再保存";
              this.GetConfig();          /* 刷新告警状态, 让顶部告警条出现 */
            }
            else if (ret === 3) msg = "参数无效：数值超出设备允许范围";
            else if (ret === 4) msg = "设备当前状态不允许改参数，请退回主页重试";
            this.$toast.fail(msg);
          } else {
            this.$toast.fail("保存失败");
          }
        })
        .catch((error) => {
          this.loading = false;
          this.$toast.fail("保存失败");
        });
    },
    onTimeConfirm() {
      var parts = String(this.timePickerCurrent).split(":");
      this.$set(this.config_wc, 6 + this.currentRtcGroup * 4, parseInt(parts[0], 10) || 0);
      this.$set(this.config_wc, 7 + this.currentRtcGroup * 4, parseInt(parts[1], 10) || 0);
      this.showTimePicker = false;
    },
    toggleWeekday(g, idx) {
      var val = this.config_wc[5 + g * 4] ^ (1 << idx);
      this.$set(this.config_wc, 5 + g * 4, val);
    },
    /* 设置预约组的独立开关 */
    setRtcEnable(g, val) {
      this.$set(this.config_wc, 4 + g * 4, val ? 1 : 0);
    },
    gotohome() {
      this.$router.go(-1);
    },
    setDefaultConfig(re, wc, sys) {
      if (re) this.config_re = re;
      if (wc) this.config_wc = wc;
      if (sys) this.config_sys = sys;
    },
  },
  mounted() {
    this.GetConfig();
  },
};
</script>

<style>
.crystal-nav {
  background: #ffffff !important;
  border-bottom: 1px solid #f1f5f9;
}

.nav-title {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
}

.alarm-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin: 10px 12px 0;
  padding: 10px 12px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 10px;
}
.alarm-banner-title {
  font-size: 13px;
  font-weight: 600;
  color: #b91c1c;
}
.alarm-banner-sub {
  margin-top: 2px;
  font-size: 11px;
  color: #dc2626;
  line-height: 1.4;
}
.alarm-banner-btn {
  flex: 0 0 auto;
  padding: 6px 12px;
  font-size: 13px;
  color: #ffffff;
  background: #ef4444;
  border: none;
  border-radius: 8px;
}
.alarm-banner-btn:disabled {
  opacity: 0.6;
}
.crystal-tabs .van-tabs__wrap {
  background: #ffffff;
}

.crystal-tabs .van-tab {
  font-weight: 500;
}

.crystal-radio .van-radio__label {
  font-size: 13px;
}
</style>

<style scoped>
.config-page {
  min-height: 100vh;
  min-height: 100dvh;
  background: linear-gradient(145deg, #f0f9ff 0%, #fdf2f8 35%, #f0fdf4 100%);
}

.content-area {
  padding: 12px;
}

.tab-content {
  padding-top: 8px;
}

/* 相邻配置卡片之间留间距, 避免贴在一起 (安全时间等) */
.tab-content > .config-block {
  margin-top: 12px;
}

.tab-content > .config-block:first-child {
  margin-top: 0;
}

.config-block {
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
  overflow: hidden;
}

.config-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  min-height: 22px;
  gap: 12px;
}

.config-item--full {
  justify-content: center;
  padding: 14px 12px;
}

.config-label {
  font-size: 14px;
  font-weight: 500;
  color: #475569;
  flex-shrink: 0;
}

.config-stepper {
  flex-shrink: 0;
}

.config-select {
  flex-shrink: 0;
}

.config-weekdays {
  display: flex;
  justify-content: space-between;
  width: 100%;
  max-width: 320px;
  gap: 4px;
}

.weekday-tag {
  width: 36px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  background: #f1f5f9;
  color: #94a3b8;
  cursor: pointer;
  transition: all 0.2s;
  user-select: none;
}

.weekday-tag:active {
  transform: scale(0.9);
}

.weekday-active {
  background: #2dd4bf;
  color: #fff;
  box-shadow: 0 2px 6px rgba(45, 212, 191, 0.25);
}

/* 区块标题 (预约设置/安全时间) */
.block-title {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  padding: 12px 16px 2px;
}

/* 预约折叠行: 名称+摘要+箭头, 行尾独立开关 */
.rtc-row {
  display: flex;
  align-items: center;
  padding: 8px 12px 8px 16px;
}

.rtc-row-main {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  cursor: pointer;
}

.rtc-row-name {
  font-size: 14px;
  font-weight: 500;
  color: #334155;
  flex-shrink: 0;
}

.rtc-row-summary {
  flex: 1;
  font-size: 13px;
  color: #334155;
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rtc-row-summary--unset {
  color: #94a3b8;
}

.rtc-row-switch {
  flex-shrink: 0;
  margin-left: 10px;
}

.rtc-detail {
  padding-bottom: 4px;
}

.config-divider {
  height: 1px;
  background: #f1f5f9;
  margin: 0 16px;
}

.config-hint {
  padding: 8px 16px 12px;
  font-size: 11px;
  color: #94a3b8;
  line-height: 1.5;
}

.config-save-wrap {
  padding: 16px 0 24px;
  display: flex;
  justify-content: center;
}

.save-btn {
  width: 100%;
  height: 44px;
  border: none;
  border-radius: 12px;
  background: linear-gradient(135deg, #2dd4bf, #14b8a6);
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: transform 0.15s, opacity 0.15s;
  box-shadow: 0 4px 12px rgba(45, 212, 191, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.save-btn:active {
  transform: scale(0.98);
  opacity: 0.9;
}

.save-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.save-btn-loading {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.time-picker-trigger {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 0.2s;
  background: #f8fafc;
}

.time-picker-trigger:active {
  border-color: #2dd4bf;
}

.time-picker-text {
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
  min-width: 44px;
  text-align: center;
}
</style>